import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { debug } from "@/lib/debug";

/**
 * Signing in as another member, for admins.
 *
 * Not to be confused with ImpersonationContext, which sits right next to it in the provider
 * tree and does something weaker: that one swaps `effectiveUserId`, so the browser reads a
 * member's rows while `auth.uid()` stays the admin. The server never learns about it, which
 * is why generating something while "viewing as" a member charges the ADMIN's credits.
 *
 * This one swaps the session itself. After a switch the browser holds the target's own access
 * and refresh tokens, so row-level security, every edge function and credit charging all see
 * the member — which is the only way to answer "what does this account actually see".
 *
 * Both survive because they answer different questions. Keeping them distinguishable is the
 * job of the two banners: "Viewing as" for the read-only one, "Signed in as" for this one.
 */

/** The admin's own session, kept so there is a way back. */
interface Stash {
  access_token: string;
  refresh_token: string;
  email: string | null;
  user_id: string;
}

/**
 * localStorage, deliberately, not sessionStorage.
 *
 * sessionStorage dies with the tab. An admin who switched and then closed that tab would be
 * stranded inside somebody else's account with no route back except signing out and in again
 * — and, on an account whose password they may not have, that is a real dead end. The admin's
 * own refresh token already lives in localStorage during normal use (see the client's
 * `storage: localStorage`), so keeping a second one beside it is not a new class of exposure.
 */
const STASH_KEY = "admin-profile-switch";

function readStash(): Stash | null {
  try {
    const raw = localStorage.getItem(STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stash;
    if (!parsed?.access_token || !parsed?.refresh_token || !parsed?.user_id) return null;
    return parsed;
  } catch {
    return null;
  }
}

interface ProfileSwitchContextValue {
  /** True while this browser is signed in as somebody else. */
  isSwitched: boolean;
  /** The admin's own email, for the way back. Null when not switched. */
  originalEmail: string | null;
  /** Whether the signed-in account may switch at all. Presentation only — the server decides. */
  canSwitch: boolean;
  switching: boolean;
  switchToUser: (targetUserId: string) => Promise<{ error: string | null }>;
  switchBack: () => Promise<void>;
}

const ProfileSwitchContext = createContext<ProfileSwitchContextValue | undefined>(undefined);

export function ProfileSwitchProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [stash, setStash] = useState<Stash | null>(() => readStash());
  const [canSwitch, setCanSwitch] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Is the account currently in the browser an admin? While switched this is the TARGET, who
  // is never an admin, so `canSwitch` correctly goes false and the search disappears — the
  // way back is driven by the stash instead, which is why they are two separate flags.
  useEffect(() => {
    let cancelled = false;

    if (!user) {
      setCanSwitch(false);
      return;
    }

    supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .maybeSingle()
      .then(({ data, error }) => {
        if (!cancelled) setCanSwitch(!!data && !error);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Other tabs. supabase-js broadcasts the new session over a BroadcastChannel, so every open
  // tab is genuinely the member the moment a switch happens — but `stash` was read from
  // localStorage when THIS tab mounted and would still be null there, so those tabs would show
  // a member's account with no banner and no way back. That is the one state this feature must
  // never produce: the banner is the whole safety mechanism. The storage event fires in every
  // OTHER tab on the origin, which is exactly the signal needed.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STASH_KEY) return;
      setStash(readStash());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // A real sign-out ends the switch too. Without this the stash outlives the session, and the
  // next person to log in on this browser would be offered a "Back to <someone else>" button.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        localStorage.removeItem(STASH_KEY);
        setStash(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const switchToUser = useCallback(async (targetUserId: string) => {
    setSwitching(true);
    try {
      // The function runs BEFORE anything is stashed or replaced. It is the only thing that
      // can fail here, and doing it first means a refusal leaves the admin exactly where they
      // were rather than half-switched with a stale stash to clean up.
      const { data, error } = await supabase.functions.invoke("admin-switch-user", {
        body: { targetUserId },
      });

      if (error || !data?.access_token || !data?.refresh_token) {
        // supabase.functions.invoke gives a generic "non-2xx status code" for every failure,
        // so dig the real message out of the response body — "Switching into another admin
        // account is not allowed" is worth showing, and the generic one tells nobody anything.
        let message = "Could not switch to that account";
        const ctx = (error as { context?: Response } | null)?.context;
        if (ctx && typeof ctx.json === "function") {
          try {
            const body = await ctx.json();
            if (typeof body?.error === "string") message = body.error;
          } catch {
            /* keep the generic message */
          }
        } else if (typeof data?.error === "string") {
          message = data.error;
        }
        debug.error("[ProfileSwitch] switch refused:", message);
        return { error: message };
      }

      const { data: current } = await supabase.auth.getSession();
      if (!current.session) return { error: "Your own session has expired — sign in again" };

      const next: Stash = {
        access_token: current.session.access_token,
        refresh_token: current.session.refresh_token,
        email: current.session.user.email ?? null,
        user_id: current.session.user.id,
      };
      localStorage.setItem(STASH_KEY, JSON.stringify(next));
      setStash(next);

      // End any read-only "View as user" first. The two are mutually exclusive in meaning —
      // you cannot be viewing as someone while BEING someone else — and leaving it on stacks
      // two fixed banners in the same corner and offers an Exit that navigates to /admin,
      // which the member you are now signed in as cannot open. ImpersonationContext keeps its
      // state in sessionStorage, which survives the reload below, so it has to be cleared here.
      sessionStorage.removeItem("vb_impersonation");

      const { error: setError } = await supabase.auth.setSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });

      if (setError) {
        localStorage.removeItem(STASH_KEY);
        setStash(null);
        return { error: setError.message };
      }

      // A full reload, not navigate().
      //
      // Half this app caches per-user data outside React state — react-query, the brand and
      // credits contexts, open chat conversations. Swapping the session in place leaves all
      // of it holding the admin's data under the member's identity, which is the exact
      // confusion this feature exists to remove. Reloading is what a real login does, and it
      // is the only way "logged in exactly like that user" is true rather than nearly true.
      window.location.assign("/dashboard");
      return { error: null };
    } finally {
      setSwitching(false);
    }
  }, []);

  const switchBack = useCallback(async () => {
    const saved = readStash();
    if (!saved) return;

    setSwitching(true);
    // Cleared first. If setSession fails the admin must land on the sign-in page with nothing
    // stale left behind, not keep a dead "Back to…" button that fails every time it is used.
    localStorage.removeItem(STASH_KEY);
    setStash(null);

    const { error } = await supabase.auth.setSession({
      access_token: saved.access_token,
      refresh_token: saved.refresh_token,
    });

    if (error) {
      debug.error("[ProfileSwitch] could not restore the admin session:", error.message);
      await supabase.auth.signOut();
      window.location.assign("/auth");
      return;
    }

    window.location.assign("/dashboard");
  }, []);

  const value = useMemo(
    () => ({
      isSwitched: !!stash,
      originalEmail: stash?.email ?? null,
      canSwitch,
      switching,
      switchToUser,
      switchBack,
    }),
    [stash, canSwitch, switching, switchToUser, switchBack],
  );

  return <ProfileSwitchContext.Provider value={value}>{children}</ProfileSwitchContext.Provider>;
}

export function useProfileSwitch() {
  const ctx = useContext(ProfileSwitchContext);
  if (!ctx) throw new Error("useProfileSwitch must be used within a ProfileSwitchProvider");
  return ctx;
}
